/**
 * familySystemDiscernment.js
 *
 * Detects pastoral-relevant family system dynamics from text.
 * NOT clinical family therapy. NOT diagnosis.
 *
 * Purpose: help Kevia adjust its response when complex family dynamics
 * are present — with more prudence, less prescription, more accompaniment.
 *
 * DISTINCT from pastoralEthicsLayer (which handles marital_crisis as a risk).
 * This detects SYSTEMIC DYNAMICS, not danger signals.
 *
 * Dynamics:
 *   triangulacion           — third party drawn into a two-person conflict
 *   aislamiento             — one family member isolated from others
 *   carga_emocional         — one person carrying the family's emotional weight
 *   agotamiento_cuidador    — caregiver exhaustion
 *   tension_silenciosa      — unspoken family tension
 *   conflicto_intergeneracional — parent-child or generational spiritual conflict
 *   dependencia             — unhealthy relational or spiritual dependency
 *   reconciliacion          — active reconciliation process
 *   ninguno                 — no clear family dynamic inferred
 */

const DYNAMIC_PATTERNS = [
  {
    dynamic: "triangulacion",
    patterns: [
      /met[eé](?:rse|rme)\s+en\s+(?:el\s+)?(?:conflicto|problema)\s+(?:de|entre)/i,
      /pide\s+que\s+(?:yo\s+)?(?:intervenga|hable\s+con|llame\s+a)/i,
      /quiere\s+que\s+(?:yo\s+)?sea\s+(?:el\s+)?mediador/i,
      /(?:su\s+)?(?:esposo|esposa|hijo|hija)\s+(?:me\s+)?pide\s+que\s+(?:yo\s+)?hable\s+con/i,
      /intermediario|mediación\s+(?:entre|de)/i,
    ],
  },
  {
    dynamic: "agotamiento_cuidador",
    patterns: [
      /cuida(?:ndo|r)\s+(?:solo|sola|a\s+todos|a\s+toda\s+la\s+familia)/i,
      /cargando\s+(?:con\s+)?todo\s+(?:solo|sola)/i,
      /nadie\s+(?:la|lo|me)\s+(?:ayuda|apoya)\s+en\s+casa/i,
      /agotad[ao]\s+de\s+cuidar/i,
      /cuidad[ao]r?\s+(?:principal|única|único)\b/i,
    ],
  },
  {
    dynamic: "tension_silenciosa",
    patterns: [
      /no\s+(?:hablan|se\s+hablan|se\s+comunican)\s+(?:entre\s+sí|bien)/i,
      /hay\s+(?:algo|una\s+tensión)\s+que\s+no\s+se\s+habla/i,
      /ambiente\s+(?:tenso|frío|difícil)\s+en\s+(?:la\s+)?(?:casa|familia|hogar)/i,
      /no\s+(?:dicen|mencionan)\s+(?:lo\s+que\s+(?:sienten|pasa))/i,
      /evitan\s+(?:hablar|el\s+tema)/i,
    ],
  },
  {
    dynamic: "conflicto_intergeneracional",
    patterns: [
      /(?:hijo[s]?|hija[s]?)\s+(?:ya\s+no\s+(?:quiere[n]?|viene[n]?)|se\s+(?:alejaron?|fue(?:ron)?))\s+de\s+la\s+(?:iglesia|fe)/i,
      /padres?\s+(?:no\s+(?:entienden?|apoyan?)|en\s+contra)/i,
      /diferencias?\s+(?:generacional|entre\s+generaciones?)/i,
      /hijos?\s+(?:rebeldes?|en\s+contra\s+(?:de\s+)?la\s+(?:fe|iglesia))/i,
      /conflicto\s+entre\s+(?:padres?\s+(?:e\s+)?hijos?|generaciones?)/i,
    ],
  },
  {
    dynamic: "dependencia",
    patterns: [
      /depend[ei](?:ente|ndo)\s+(?:de\s+(?:mí|la\s+iglesia|el\s+pastor))/i,
      /(?:no\s+puede\s+hacer\s+nada|todo\s+lo\s+consulta)\s+sin\s+(?:mí|el\s+pastor)/i,
      /muy\s+apegad[ao]\s+(?:a\s+mí|al\s+pastor)/i,
      /depend[ei](?:ncia|ente)\s+(?:emocional|espiritual|pastoral)/i,
    ],
  },
  {
    dynamic: "aislamiento",
    patterns: [
      /(?:se\s+(?:aisló|alejó|encerró|distanció))\s+(?:de\s+(?:la\s+familia|todos|nosotros))/i,
      /(?:la\s+familia|(?:su\s+)?esposo|(?:su\s+)?esposa)\s+(?:lo|la)\s+(?:aisló|rechazó|alejó)/i,
      /sin\s+(?:familia|apoyo\s+familiar|red\s+de\s+apoyo)/i,
      /nadie\s+de\s+(?:su\s+)?familia\s+(?:lo|la)\s+apoya/i,
    ],
  },
  {
    dynamic: "carga_emocional",
    patterns: [
      /cargando\s+(?:con\s+el\s+peso|con\s+los\s+problemas)\s+de\s+(?:toda\s+la\s+)?familia/i,
      /(?:lleva|carga)\s+(?:todos\s+los\s+problemas|el\s+dolor)\s+(?:de|solo)/i,
      /todo\s+(?:el\s+peso|la\s+carga)\s+(?:recae|cae)\s+sobre\s+(?:él|ella)/i,
    ],
  },
  {
    dynamic: "reconciliacion",
    patterns: [
      /reconcili(?:aci[oó]n|arse|ando)\s+(?:con\s+|en\s+)?(?:su\s+)?(?:familia|esposo|esposa|hijo|hija|padre|madre)/i,
      /quiere\s+reconciliarse|restaurar\s+la\s+relaci[oó]n\s+(?:familiar|con)/i,
      /dar\s+(?:un\s+)?paso\s+de\s+reconciliaci[oó]n/i,
    ],
  },
];

/**
 * @param {{
 *   text:    string,
 *   emotion: object|null,
 *   intent:  string,
 * }} params
 * @returns {{
 *   dynamic:     string,
 *   instruction: string|null,
 * }}
 */
export function detectFamilyDynamics({ text, emotion, intent }) {
  const t = text ?? "";

  for (const { dynamic, patterns } of DYNAMIC_PATTERNS) {
    if (patterns.some((p) => p.test(t))) {
      return { dynamic, instruction: _instruction(dynamic) };
    }
  }

  return { dynamic: "ninguno", instruction: null };
}

const INSTRUCTIONS = {
  triangulacion:
    "Hay señales de triangulación familiar. No te conviertas en intermediario entre partes. Acompaña con prudencia y sin tomar partido.",
  agotamiento_cuidador:
    "Hay señales de agotamiento del cuidador. Antes de proponer pasos, reconoce la carga que lleva esta persona.",
  tension_silenciosa:
    "Hay tensión familiar no verbalizada. No la fuerces a la superficie. Escucha más de lo que propones.",
  conflicto_intergeneracional:
    "Conflicto intergeneracional presente. No tomes partido. Acompaña con paciencia el proceso de cada generación.",
  dependencia:
    "Hay señales de dependencia pastoral o relacional excesiva. Favorece la autonomía — acompaña sin crear apego.",
  aislamiento:
    "La persona está aislada relacionalmente. Prioriza presencia y contacto antes de acción o dirección.",
  carga_emocional:
    "Alguien lleva demasiado peso emocional de la familia. Reconoce esa carga antes de avanzar.",
  reconciliacion:
    "Proceso de reconciliación familiar activo. Sostén el movimiento hacia la restauración sin apresurar.",
};

function _instruction(dynamic) {
  return INSTRUCTIONS[dynamic] ?? null;
}
